// apps/frontend/admin-ui/src/pages/table-limits.ts
import { AdminApiError, type AdminClient, type TableOverride } from '@apx/admin-sdk';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import {
  tableSchema,
  TABLE_SCHEMA_BY_GAME,
  deriveChips,
  CHIP_MULTIPLIERS,
  CHIP_SLOTS,
  TABLE_DEFAULTS,
  TABLE_DEFAULTS_BY_CURRENCY,
  MAX_PAYOUT_DEFAULTS_BY_CURRENCY,
  SUPPORTED_CURRENCIES,
  resolveCurrencyTable,
  type IntegratorGameConfig,
} from '@apx/integrator-config/schemas';
import { showToast } from '../toast.js';
import { formatChip, formatMoney } from '@apx/money';
import { moneyUnitHint, setMoneyInputCurrency } from '../money-input.js';
import {
  chipWindowIssues,
  limitInputValue,
  readLimitInput,
  stakeGridIssues,
} from './table-limits-money.js';

export interface TableLimitsPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** True when editing the global provider default (no override/inherited split);
   *  false when editing one integrator's override on top of the provider default. */
  isProviderScope: boolean;
}

/** Full per-currency override map for one (integrator, game). */
type CurrencyTableMap = Record<string, TableOverride>;

/**
 * The schema this form validates a save against: the game's OWN table schema,
 * read from the registry the API validates the same write with.
 *
 * This used to be a second per-game map kept here by hand, and a game missing
 * from it fell through to the generic `tableSchema`. That one does not check
 * that every chip sits inside [minBet, maxBet], nor a game's stake grid (the
 * chip values on which `floor(stake x bps / 10000)` is exact), so the form let
 * the value through and the API refused it with no inline error against the
 * offending field. One registry, so a new game needs no entry here.
 */
export function tableSchemaFor(gameId: string): typeof tableSchema {
  return (TABLE_SCHEMA_BY_GAME[gameId] ?? tableSchema) as typeof tableSchema;
}

export async function renderTableLimitsPage(
  root: HTMLElement,
  deps: TableLimitsPageDeps,
): Promise<void> {
  const { gameId } = deps;
  // Side-bet cap applies to every game whose module implements `maxBetFor` and
  // classifies high-multiplier positions — the orchestrator reads
  // `limits.sideBetMaxBet` through it for the per-spot cap.
  //
  // Baccarat was missing here while its module DOES classify Tie (8:1) and both
  // Pair cells (11:1) as side bets. So the field never rendered, the cap stayed
  // undefined and those spots fell back to `maxBet` — 1,000,000,000 on IDR,
  // i.e. a 9,000,000,000 payout on Tie — and, because the save handler rebuilds
  // the payload from the inputs, any cap set through the API was silently
  // dropped by the next save from this page.
  const SIDE_BET_SPOTS: Record<string, string> = {
    sicbo: 'specific-triple bets (Any Triple + each three-of-a-kind spot)',
    dragontiger: 'Tie, suit, and sum-range spots',
    baccarat: 'Tie (8:1) and both Pair spots (11:1)',
  };
  const sideBetHelp = SIDE_BET_SPOTS[gameId] ?? '';
  const hasSideBets = sideBetHelp !== '';
  // Per-player, per-round payout cap: only games whose player picks the
  // multiplier enforce it, and those are exactly the games with a baked
  // default. Without the field here, a save from this page would drop a cap
  // set through the API, the same way it once dropped baccarat's side cap.
  const payoutDefaults = MAX_PAYOUT_DEFAULTS_BY_CURRENCY[gameId];
  const hasPayoutCap = payoutDefaults !== undefined;
  const slots = CHIP_SLOTS[gameId] ?? deriveChips(gameId, 1).length;
  const mults = CHIP_MULTIPLIERS[gameId] ?? [];

  // ── Load the layered config (this layer's own override + the provider default
  // beneath it), the platform currency registry (codes + symbols), and the
  // integrator's enabled currencies, in parallel. `tableByCurrency` is the
  // native-currency surface we edit; the legacy flat `table` is read-only so an
  // existing IDR override still shows on first open.
  const [inh, registry, fiatList] = await Promise.all([
    deps.client.getConfigInheritance(deps.integratorId, gameId),
    deps.client.listCurrencies().catch(() => []),
    deps.client.listIntegratorFiatCurrencies(deps.integratorId).catch(() => []),
  ]);

  const ownCfg = inh.ownOverride as IntegratorGameConfig;
  const providerCfg = inh.providerDefault as IntegratorGameConfig;
  // The layer being edited (integrator override, or the global default itself in
  // provider scope) and the provider layer shown beneath it for reference.
  let byCurrency: CurrencyTableMap = (ownCfg.tableByCurrency as CurrencyTableMap | null) ?? {};
  const legacyTable = (ownCfg.table as TableOverride | null) ?? undefined;
  const providerByCur = (providerCfg.tableByCurrency as CurrencyTableMap | null) ?? {};
  const providerLegacy = (providerCfg.table as TableOverride | null) ?? undefined;

  // Currency symbol per code from the platform registry (Rp / ฿ / $ …).
  const symbols = new Map(registry.map((c) => [c.code, c.symbol ?? c.code]));
  const symbolOf = (cur: string): string => symbols.get(cur) ?? cur;
  // `v` is a stored amount: minor units of `cur` (USD cents). Shown in the
  // currency, with every decimal it has — "$ 1.50", "Rp 1,000".
  const fmtMoney = (v: number, cur: string): string =>
    `${symbolOf(cur)} ${formatMoney(v, cur, { compact: false })}`;

  // Currency set = platform-registry codes (the source of truth) ∪ integrator
  // fiats ∪ any currency already overridden here or by the provider. IDR first.
  const currencies = currencyList(registry, fiatList, byCurrency, providerByCur);

  root.innerHTML = `
    <style>
      .tl-cur { display:flex; flex-direction:column; gap:6px; font-size:12px; color:var(--text-dim,#8893a8); }
      .tl-cur select { min-width:220px; }
      .tl-base-wrap { max-width:640px; }
      #tl-base { font-size:18px; font-weight:700; }
      .tl-chipset { display:flex; flex-wrap:wrap; gap:12px; align-items:flex-end; }
      /* .tl-chipset display:flex would otherwise beat the UA [hidden] rule. */
      #tl-chips-custom[hidden] { display:none; }
      .tl-chip {
        display:inline-flex; align-items:center; justify-content:center;
        min-width:74px; padding:9px 16px; border-radius:999px;
        border:1px solid var(--accent,#f5c84b); background:rgba(245,200,75,0.1);
        font-weight:600; font-size:13px; color:var(--text,#e8eaf0);
      }
      .tl-slot { display:flex; flex-direction:column; gap:6px; font-size:12px; color:var(--text-dim,#8893a8); }
      .tl-slot input { width:120px; text-align:center; font-weight:600; }
      .tl-custom { display:inline-flex; align-items:center; gap:8px; margin:14px 0 4px; font-size:13px; cursor:pointer; }
      .tl-badge { display:inline-block; padding:3px 10px; border-radius:999px; font-size:11px; font-weight:700; letter-spacing:0.4px; }
      .tl-badge.override { background:rgba(245,200,75,0.18); color:var(--accent,#f5c84b); }
      .tl-badge.inherited { background:rgba(120,130,150,0.16); color:var(--text-dim,#8893a8); }
      .tl-fmt { display:block; margin-top:4px; font-size:12px; color:var(--accent,#f5c84b); font-weight:600; font-variant-numeric:tabular-nums; }
      .tl-unit { font-size:11px; font-weight:400; color:var(--text-dim,#8893a8); }
      .tl-ref { font-size:12.5px; color:var(--text-dim,#8893a8); font-variant-numeric:tabular-nums; margin:0; }
      .tl-layout { display:grid; grid-template-columns:210px minmax(0,1fr); gap:16px; align-items:stretch; margin-bottom:16px; }
      @media (max-width:820px){ .tl-layout{ grid-template-columns:1fr; } }
      .tl-cur-nav { display:flex; flex-direction:column; padding:16px 12px; margin-bottom:0; }
      .tl-cur-nav .lbl { font-size:11px; letter-spacing:0.5px; text-transform:uppercase; color:var(--text-dim,#8893a8); margin:0 6px 10px; }
      .tl-nav-note { margin-top:auto; padding-top:14px; }
      .tl-content { display:flex; flex-direction:column; gap:16px; }
      .tl-content > .card { margin:0; }
      .tl-cur-tab { display:flex; width:100%; align-items:center; justify-content:space-between; gap:8px; text-align:left; padding:9px 12px; margin-bottom:4px; border-radius:10px; border:1px solid transparent; background:transparent; color:var(--text,#e8eaf0); font-weight:600; font-size:14px; cursor:pointer; }
      .tl-cur-tab:hover { background:rgba(255,255,255,0.04); }
      .tl-cur-tab.active { background:rgba(245,200,75,0.12); border-color:var(--accent,#f5c84b); color:var(--accent,#f5c84b); }
      .tl-cur-tab .dot { width:7px; height:7px; border-radius:50%; background:var(--accent,#f5c84b); flex:0 0 auto; }
    </style>
    <h1 class="page-title">Table limits — ${escapeLabel(gameId)}</h1>
    <p class="page-sub">Per-currency bet bounds + chip rail. Every amount is typed and shown in the currency itself: USD in dollars with cents (1.50), every other currency in whole units. Set one <strong>base bet</strong> per currency; the chip ladder derives as base × ${mults.length ? `[${mults.join(', ')}]` : 'the game ladder'}. Server-enforced at bet accept; changes go through two-admin approval.</p>

    <div class="tl-layout">
      <aside class="card tl-cur-nav">
        <div class="lbl">Currency</div>
        ${currencies
          .map(
            (c) => `<button type="button" class="tl-cur-tab" data-cur="${c}">
          <span>${symbolOf(c)} · ${c}</span>
          ${!deps.isProviderScope && byCurrency[c] ? '<span class="dot" title="override set"></span>' : ''}
        </button>`,
          )
          .join('')}
        <p class="muted tl-nav-note" style="margin:0 6px;font-size:11px">From the platform <a href="/currencies">Currencies</a> registry.</p>
      </aside>
      <div class="tl-content">

    <div class="card">
      <h2>Bet limits · <span id="tl-cur-label"></span> ${deps.isProviderScope ? '' : '<span id="tl-inherit" class="tl-badge"></span>'}</h2>
      <div class="form-grid tl-base-wrap">
        <label>Base bet (min) <span class="tl-unit"></span>
          <input id="tl-base" type="number" min="1" step="1" />
          <span class="tl-fmt" id="tl-base-fmt"></span>
        </label>
        <label>Max bet <span class="tl-unit"></span>
          <input id="tl-max" type="number" min="1" step="1" />
          <span class="tl-fmt" id="tl-max-fmt"></span>
        </label>
        ${
          hasSideBets
            ? `<label>Side-bet max <span class="tl-unit"></span>
          <input id="tl-side" type="number" min="1" step="1" />
          <span class="tl-fmt" id="tl-side-fmt"></span>
        </label>`
            : ''
        }
        ${
          hasPayoutCap
            ? `<label>Max payout per round <span class="tl-unit"></span>
          <input id="tl-payout" type="number" min="1" step="1" />
          <span class="tl-fmt" id="tl-payout-fmt"></span>
        </label>`
            : ''
        }
      </div>
      ${
        hasSideBets
          ? `<p class="muted" style="margin:12px 0 0;font-size:12px">Tighter ceiling for high-multiplier side bets (${sideBetHelp}). Must sit within [base, max]. Leave equal to max for no extra limit.</p>`
          : ''
      }
      ${
        hasPayoutCap
          ? `<p class="muted" style="margin:12px 0 0;font-size:12px">Most one player can win in one round: the sum of stake × target over all their bets in it, stake included. A bet that would take the total past it is refused. Leave blank for the currency's default.</p>`
          : ''
      }
    </div>

    <div class="card">
      <h2>Chip rail</h2>
      <div id="tl-chips-preview" class="tl-chipset"></div>
      <label class="tl-custom"><input type="checkbox" id="tl-customize" /> Customize chip values</label>
      <div id="tl-chips-custom" class="tl-chipset" hidden>
        ${Array.from(
          { length: slots },
          (_, i) => `
          <label class="tl-slot">Chip ${i + 1}
            <input data-slot="${i}" type="number" min="1" step="1" />
          </label>`,
        ).join('')}
      </div>
      <p class="muted" style="margin:10px 0 0;font-size:12px">${slots} chip slots, fixed count. Derived from the base bet by default; toggle customize to override individual slots (ascending &amp; unique, each inside [base, max]). Rendered in-game as disc + label, so any values work.</p>
    </div>

    ${
      deps.isProviderScope
        ? ''
        : `<div class="card">
      <h2>Provider default · <span id="tl-ref-cur"></span></h2>
      <p class="tl-ref" id="tl-provider-ref">…</p>
      <p class="muted" style="margin:8px 0 0;font-size:12px">The baseline this integrator inherits when it doesn't override. Edit it in the <em>Provider · all integrators</em> scope.</p>
    </div>`
    }
      </div>
    </div>

    <div class="card">
      <div class="row" style="gap:10px">
        <button id="tl-save" class="primary">Propose change</button>
        <button id="tl-reset" class="ghost">Reset to defaults</button>
      </div>
      <p id="tl-msg" class="muted"></p>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="tl-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const curTabEls = Array.from(root.querySelectorAll<HTMLButtonElement>('.tl-cur-tab'));
  const curLabelEl = root.querySelector<HTMLElement>('#tl-cur-label')!;
  let activeCur = currencies[0] ?? 'IDR';
  const baseEl = root.querySelector<HTMLInputElement>('#tl-base')!;
  const maxEl = root.querySelector<HTMLInputElement>('#tl-max')!;
  const sideEl = root.querySelector<HTMLInputElement>('#tl-side'); // DT + sicbo; null elsewhere
  const payoutEl = root.querySelector<HTMLInputElement>('#tl-payout'); // payout-cap games only
  const previewEl = root.querySelector<HTMLElement>('#tl-chips-preview')!;
  const customizeEl = root.querySelector<HTMLInputElement>('#tl-customize')!;
  const customWrapEl = root.querySelector<HTMLElement>('#tl-chips-custom')!;
  const slotEls = Array.from(root.querySelectorAll<HTMLInputElement>('input[data-slot]'));
  // "· USD, up to 2 decimals" beside each bet-limit label, per active currency.
  const unitEls = Array.from(root.querySelectorAll<HTMLElement>('.tl-unit'));
  const msgEl = root.querySelector<HTMLElement>('#tl-msg')!;
  const baseFmtEl = root.querySelector<HTMLElement>('#tl-base-fmt')!;
  const maxFmtEl = root.querySelector<HTMLElement>('#tl-max-fmt')!;
  const sideFmtEl = root.querySelector<HTMLElement>('#tl-side-fmt'); // null when no side bet
  const payoutFmtEl = root.querySelector<HTMLElement>('#tl-payout-fmt'); // null without a cap
  const inheritEl = root.querySelector<HTMLElement>('#tl-inherit'); // null in provider scope
  const refCurEl = root.querySelector<HTMLElement>('#tl-ref-cur'); // null in provider scope
  const providerRefEl = root.querySelector<HTMLElement>('#tl-provider-ref'); // null in provider scope

  /** Live currency-formatted readouts under the bet-limit inputs ("Rp 1,000",
   *  "$ 1.50"). A value that is not an amount in the currency — too many
   *  decimals, not a number — reads as a dash rather than a rounded guess. */
  function updateMoneyFmt(): void {
    const cur = currentCurrency();
    const readout = (el: HTMLInputElement): string => {
      const minor = readLimitInput(el.value, cur);
      return minor === null ? '—' : fmtMoney(minor, cur);
    };
    baseFmtEl.textContent = readout(baseEl);
    maxFmtEl.textContent = readout(maxEl);
    if (sideFmtEl && sideEl) sideFmtEl.textContent = readout(sideEl);
    if (payoutFmtEl && payoutEl) {
      payoutFmtEl.textContent = payoutEl.value.trim() === '' ? 'default' : readout(payoutEl);
    }
  }

  /** Override/inherited badge + provider-default reference for the current
   *  currency (integrator scope only). */
  function updateInheritance(cur: string): void {
    if (deps.isProviderScope || !inheritEl) return;
    const overridden = isOverridden(cur);
    inheritEl.className = `tl-badge ${overridden ? 'override' : 'inherited'}`;
    inheritEl.textContent = overridden ? 'OVERRIDE' : 'INHERITED';
    const pd = providerFor(cur);
    if (refCurEl) refCurEl.textContent = cur;
    if (providerRefEl) {
      const chipsStr = pd.chips.map((v) => `${symbolOf(cur)} ${formatChip(v, cur)}`).join(', ');
      providerRefEl.textContent =
        `Base ${fmtMoney(pd.minBet, cur)} · Max ${fmtMoney(pd.maxBet, cur)}` +
        (pd.sideBetMaxBet !== undefined ? ` · Side-bet ${fmtMoney(pd.sideBetMaxBet, cur)}` : '') +
        (hasPayoutCap && pd.maxPayout !== undefined
          ? ` · Max payout ${fmtMoney(pd.maxPayout, cur)}`
          : '') +
        ` · Chips ${chipsStr}`;
    }
  }

  function currentCurrency(): string {
    return activeCur;
  }

  type Limits = {
    minBet: number;
    maxBet: number;
    chips: number[];
    sideBetMaxBet?: number;
    maxPayout?: number;
  };

  /** Effective limits for a currency = own override (this layer) → provider
   *  default → legacy IDR table → baked default (per-field, never undefined). */
  function effectiveFor(cur: string): Limits {
    const mergedByCur: CurrencyTableMap = { ...providerByCur, ...byCurrency };
    const mergedLegacy = legacyTable ?? providerLegacy;
    const base = resolveCurrencyTable(
      { table: mergedLegacy, tableByCurrency: mergedByCur } as IntegratorGameConfig,
      gameId,
      cur,
    );
    const sideBetMaxBet =
      mergedByCur[cur]?.sideBetMaxBet ?? (cur === 'IDR' ? mergedLegacy?.sideBetMaxBet : undefined);
    return { ...base, sideBetMaxBet };
  }

  /** The provider-default layer beneath this one, for the inherited reference. */
  function providerFor(cur: string): Limits {
    const base = resolveCurrencyTable(
      { table: providerLegacy, tableByCurrency: providerByCur } as IntegratorGameConfig,
      gameId,
      cur,
    );
    const sideBetMaxBet =
      providerByCur[cur]?.sideBetMaxBet ??
      (cur === 'IDR' ? providerLegacy?.sideBetMaxBet : undefined);
    return { ...base, sideBetMaxBet };
  }

  /** Does THIS layer set the currency (override) vs inherit it from below? */
  function isOverridden(cur: string): boolean {
    return Boolean(byCurrency[cur]) || (cur === 'IDR' && Boolean(legacyTable));
  }

  function renderPreview(chips: number[]): void {
    const cur = currentCurrency();
    previewEl.innerHTML = chips
      .map((v) => `<span class="tl-chip">${symbolOf(cur)} ${formatChip(v, cur)}</span>`)
      .join('');
  }

  /** The base bet as typed, in minor units; 0 while it is not a valid amount. */
  function baseMinor(): number {
    return readLimitInput(baseEl.value, currentCurrency()) ?? 0;
  }

  /** Live-refresh the derived preview from the current base (only while NOT
   *  customizing — a custom ladder is authoritative and left untouched). */
  function refreshDerivedPreview(): void {
    if (customizeEl.checked) return;
    renderPreview(deriveChips(gameId, baseMinor()));
  }

  function fillForm(value: Limits): void {
    // Stored amounts are minor units; the inputs hold the currency's own
    // amount ("1.50" for 150 cents), stepped by its smallest unit.
    const cur = currentCurrency();
    const show = (minor: number | undefined): string =>
      minor === undefined ? '' : limitInputValue(minor, cur);
    for (const el of [baseEl, maxEl, sideEl, payoutEl, ...slotEls]) {
      if (el) setMoneyInputCurrency(el, cur);
    }
    unitEls.forEach((el) => {
      el.textContent = `· ${moneyUnitHint(cur)}`;
    });
    baseEl.value = show(value.minBet);
    maxEl.value = show(value.maxBet);
    if (sideEl) sideEl.value = show(value.sideBetMaxBet ?? value.maxBet);
    if (payoutEl) payoutEl.value = show(value.maxPayout);
    const derived = deriveChips(gameId, value.minBet);
    const isCustom = !arraysEqual(value.chips, derived);
    customizeEl.checked = isCustom;
    customWrapEl.hidden = !isCustom;
    if (isCustom) {
      slotEls.forEach((el, i) => {
        el.value = show(value.chips[i] ?? derived[i]);
      });
      renderPreview(value.chips);
    } else {
      renderPreview(derived);
    }
  }

  function loadCurrency(cur: string): void {
    activeCur = cur;
    curTabEls.forEach((t) => t.classList.toggle('active', t.dataset.cur === cur));
    curLabelEl.textContent = cur;
    msgEl.textContent = '';
    msgEl.className = 'muted';
    fillForm(effectiveFor(cur));
    updateMoneyFmt();
    updateInheritance(cur);
  }

  loadCurrency(activeCur);

  curTabEls.forEach((t) => t.addEventListener('click', () => loadCurrency(t.dataset.cur!)));
  baseEl.addEventListener('input', () => {
    refreshDerivedPreview();
    updateMoneyFmt();
  });
  maxEl.addEventListener('input', updateMoneyFmt);
  sideEl?.addEventListener('input', updateMoneyFmt);
  payoutEl?.addEventListener('input', updateMoneyFmt);
  customizeEl.addEventListener('change', () => {
    customWrapEl.hidden = !customizeEl.checked;
    if (customizeEl.checked) {
      // Seed the slot inputs from whatever's currently previewed (the derived
      // ladder) so the operator starts from a sane, ascending set.
      const cur = currentCurrency();
      const derived = deriveChips(gameId, baseMinor());
      slotEls.forEach((el, i) => {
        if (!el.value && derived[i] !== undefined) el.value = limitInputValue(derived[i]!, cur);
      });
    } else {
      refreshDerivedPreview();
    }
  });

  root.querySelector<HTMLButtonElement>('#tl-reset')!.addEventListener('click', () => {
    const cur = currentCurrency();
    const def = TABLE_DEFAULTS_BY_CURRENCY[gameId]?.[cur] ?? TABLE_DEFAULTS[gameId];
    if (!def) return;
    fillForm({
      minBet: def.minBet,
      maxBet: def.maxBet,
      chips: def.chips,
      maxPayout: payoutDefaults?.[cur],
    });
    updateMoneyFmt();
    msgEl.textContent = `Reset ${cur} to baked defaults (not saved).`;
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#tl-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const cur = currentCurrency();
    const fail = (messages: string[]): void => {
      msgEl.textContent = messages.join(' • ');
      msgEl.className = 'error';
    };

    // The inputs hold amounts in the currency; the config stores minor units.
    // An input that is not an amount in this currency (not a number, or more
    // decimals than it has) is refused here — rounding it would save a limit
    // the operator did not type.
    const invalid: string[] = [];
    const read = (label: string, el: HTMLInputElement): number => {
      const minor = readLimitInput(el.value, cur);
      if (minor === null) invalid.push(`${label}: enter an amount in ${moneyUnitHint(cur)}`);
      return minor ?? 0;
    };
    const base = read('Base bet', baseEl);
    const maxBet = read('Max bet', maxEl);
    const side = sideEl ? read('Side-bet max', sideEl) : undefined;
    // Blank means "the currency's default", so it is left out rather than read.
    const payout =
      payoutEl && payoutEl.value.trim() !== '' ? read('Max payout', payoutEl) : undefined;
    const chips = customizeEl.checked
      ? slotEls.map((el, i) => read(`Chip ${i + 1}`, el))
      : deriveChips(gameId, base);
    if (invalid.length > 0) {
      fail(invalid);
      return;
    }
    const value: TableOverride = {
      minBet: base,
      maxBet,
      // Side cap only for games in SIDE_BET_SPOTS, and only when it tightens
      // below max (equal to max = "no extra limit", not worth persisting).
      // NOTE: this rebuilds the whole currency entry, so a game absent from
      // SIDE_BET_SPOTS drops any stored `sideBetMaxBet` on save.
      ...(side !== undefined && side !== maxBet ? { sideBetMaxBet: side } : {}),
      ...(payout !== undefined ? { maxPayout: payout } : {}),
      chips,
    };

    // Rules whose message carries an amount are checked here first, so the
    // operator reads them in the currency ("a multiple of 1.00 USD") rather
    // than as the schema's count of minor units. The schema below is the same
    // rules and still has the last word.
    const amounts = { minBet: base, maxBet, chips };
    const amountIssues = [
      ...stakeGridIssues(gameId, amounts, cur),
      ...(TABLE_SCHEMA_BY_GAME[gameId] ? chipWindowIssues(amounts, cur) : []),
    ];
    if (amountIssues.length > 0) {
      fail(amountIssues);
      return;
    }

    const schema = tableSchemaFor(gameId);
    const parsed = schema.safeParse(value);
    if (!parsed.success) {
      fail(parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`));
      return;
    }

    // proposeSurface replaces the WHOLE tableByCurrency map, so merge the edited
    // currency into the current map before proposing.
    const nextMap: CurrencyTableMap = { ...byCurrency, [cur]: parsed.data as TableOverride };
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        gameId,
        'tableByCurrency',
        nextMap,
      );
      msgEl.textContent = `Pending approval ${res.approval.id} for ${cur}.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent =
        err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function reloadSurface(): Promise<void> {
    const fresh = await deps.client.getSurface(deps.integratorId, gameId, 'tableByCurrency');
    byCurrency = (fresh.value as CurrencyTableMap | null) ?? {};
    loadCurrency(currentCurrency());
  }

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'tableByCurrency' && a.gameId === gameId);
    const container = root.querySelector<HTMLElement>('#tl-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th>Currencies</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td><code>${escapeLabel(currencyKeys(a.pendingConfig))}</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          await refreshPending();
          await reloadSurface();
        } catch (err) {
          showToast(
            err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err),
            'error',
          );
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(
            err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err),
            'error',
          );
        }
      });
    });
  }

  await refreshPending();
}

/** IDR-first, then alphabetical. Platform-registry codes (enabled) are the
 *  source of valid currencies; unioned with the integrator's fiats and any
 *  currency already overridden at either layer (never hide an existing config).
 *  Falls back to the baked SUPPORTED_CURRENCIES only if the registry is empty. */
function currencyList(
  registry: Array<{ code: string; enabled: boolean }>,
  fiat: Array<{ fiatCurrency: string }>,
  byCurrency: Record<string, unknown>,
  providerByCur: Record<string, unknown>,
): string[] {
  const enabled = registry.filter((c) => c.enabled).map((c) => c.code);
  const set = new Set<string>(enabled.length ? enabled : ['IDR', ...SUPPORTED_CURRENCIES]);
  for (const f of fiat) set.add(f.fiatCurrency);
  for (const k of Object.keys(byCurrency)) set.add(k);
  for (const k of Object.keys(providerByCur)) set.add(k);
  return [...set].sort((a, b) => (a === 'IDR' ? -1 : b === 'IDR' ? 1 : a.localeCompare(b)));
}

/** Comma list of the currency keys inside a pending tableByCurrency payload. */
function currencyKeys(pending: unknown): string {
  if (pending && typeof pending === 'object')
    return Object.keys(pending as object).join(', ') || '—';
  return '—';
}

function arraysEqual(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function escapeLabel(s: string): string {
  return s.replace(/[&<>"']/g, '');
}
